import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { XMarkIcon, TrashIcon } from '@heroicons/react/24/outline';
import {
  CartItem,
  getCurrentCartState,
  subscribeToCartState,
  removeFromCart,
  updateCartItemQuantity,
  initializeCart
} from '../utils/cartManager';
import { calculateShippingAndTax } from '../utils/computeUtils';
import { request } from '../utils/request';

const CartPage: React.FC = () => {
  const [cartItemsState, setCartItemsState] = useState<CartItem[]>([]);
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [shipping, setShipping] = useState(4.99);
  const [tax, setTax] = useState(0);
  const [isCalculating, setIsCalculating] = useState(false);

  // 计算运费和税费的函数
  const handleCalculateShippingAndTax = async (subtotal: number) => {
    setIsCalculating(true);
    try {
      const result = await calculateShippingAndTax(subtotal);
      setShipping(result.shipping);
      setTax(result.tax);

      if (result.error) {
        console.error('计算运费和税费失败:', result.error);
      }
    } finally {
      setIsCalculating(false);
    }
  };

  // 监听购物车数据变化
  useEffect(() => {
    const updateCartData = () => {
      const newCartState = getCurrentCartState();
      setCartItemsState(newCartState);

      // 计算新的 subtotal 并更新运费和税费
      const newSubtotal = newCartState.reduce((sum, item) => sum + (item.product.basePrice * item.quantity), 0);
      handleCalculateShippingAndTax(newSubtotal);
    };

    // 订阅购物车状态变化
    const unsubscribe = subscribeToCartState(updateCartData);

    // 初始加载购物车数据
    const newCartState = getCurrentCartState();
    setCartItemsState(newCartState);

    // 计算初始运费和税费
    const initialSubtotal = newCartState.reduce((sum, item) => sum + (item.product.basePrice * item.quantity), 0);
    handleCalculateShippingAndTax(initialSubtotal);

    // 从本地缓存读取 COUPON
    try {
      const cachedCoupon = localStorage.getItem('COUPON');
      console.log('Cached coupon:', cachedCoupon);
      if (cachedCoupon?.trim()) {
        console.log('Cached coupon:', cachedCoupon);
        setCouponCode(cachedCoupon);
        applyCouponCode(cachedCoupon);
        setTimeout(() => {
          handleCalculateShippingAndTax(subtotal);
        }, 1000);
      }
    } catch (e) {
      console.warn('Failed to read cached coupon:', e);
    }

    setIsLoading(false);

    return unsubscribe;
  }, []);

  const updateQuantity = async (id: number, newQuantity: number) => {
    if (newQuantity <= 0) {
      // Remove item if quantity is 0 or negative
      await removeFromCart(id);
    } else if (newQuantity > 99) {
      // Limit maximum quantity to 99
      alert('Maximum quantity is 99');
      return;
    } else {
      // Update quantity
      await updateCartItemQuantity(id, newQuantity);
    }
    // Cart data will be updated automatically via subscription
  };

  const decreaseQuantity = async (id: number, currentQuantity: number) => {
    if (currentQuantity <= 1) {
      // Remove item if quantity is 1 or less
      await removeFromCart(id);
    } else {
      // Decrease quantity
      await updateCartItemQuantity(id, currentQuantity - 1);
    }
    // Cart data will be updated automatically via subscription
  };

  const increaseQuantity = async (id: number, currentQuantity: number) => {
    if (currentQuantity >= 99) {
      // Limit maximum quantity to 99
      alert('Maximum quantity is 99');
    } else {
      // Increase quantity
      await updateCartItemQuantity(id, currentQuantity + 1);
    }
    // Cart data will be updated automatically via subscription
  };

  const removeItem = async (id: number) => {
    await removeFromCart(id);
    // Cart data will be updated automatically via subscription
  };

  const subtotal = cartItemsState.reduce((sum, item) => sum + (item.product.basePrice * item.quantity), 0);
  const discount = couponApplied ? couponDiscount : 0;
  const total = subtotal + shipping + tax - discount;

  const applyCouponCode = async (code: string) => {
    try {
      console.log('Applying coupon code:', code);
      const data: any = await request({
        url: `coupon/check?code=${code}`,
        method: 'GET'
      });

      if (data.success && data.result) {
        const { discount, type } = data.result;
        
        // Recalculate subtotal here to ensure we have the latest value
        // Note: cartItemsState might be stale if called from useEffect
        // We should use getCurrentCartState() to get the freshest data
        const currentCart = getCurrentCartState();
        const currentSubtotal = currentCart.reduce((sum, item) => sum + (item.product.basePrice * item.quantity), 0);

        let discountAmount = 0;
        if (type === 'percentage') {
          discountAmount = (currentSubtotal * discount) / 100;
        } else {
          discountAmount = discount;
        } 
        setCouponApplied(true);
        setCouponDiscount(discountAmount);
        return discountAmount;
      } else {
        alert(data.message || 'Invalid coupon code');
        localStorage.removeItem('COUPON');
        setCouponApplied(false);
        setCouponDiscount(0);
      }
      return 0;
    } catch (error) {
      console.error('Error applying coupon:', error);
      alert('Failed to apply coupon. Please try again.');
      return 0;
    }
  };

  const handleApplyCoupon = async () => {
    if (couponCode.trim()) {
      const discountAmount = await applyCouponCode(couponCode);
      if (discountAmount > 0) {
        // Update total price
        handleCalculateShippingAndTax(subtotal); 
      }
    }
  };

  const handleRemoveCoupon = () => {
    setCouponApplied(false);
    setCouponDiscount(0);
    setCouponCode('');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900">Shopping Cart</h1>
            <p className="text-gray-600 mt-1">{cartItemsState.length} items</p>
          </div>

          {isLoading ? (
            /* Loading State */
            <div className="px-6 py-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderBottomColor: '#2e3a47' }}></div>
              <p className="text-gray-600">Loading cart...</p>
            </div>
          ) : cartItemsState.length === 0 ? (
            /* Empty Cart */
            <div className="px-6 py-12 text-center">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">🛒</span>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Your cart is empty</h2>
              <p className="text-gray-600 mb-6">Start shopping to add items to your cart</p>
              <Link
                to="/products"
                className="inline-block text-white px-8 py-3 rounded-lg transition-colors font-medium"
                style={{ backgroundColor: '#2e3a47' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
              >
                Start Shopping
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-6">
              {/* Cart Items */}
              <div className="lg:col-span-2">
                <div className="space-y-4">
                  {cartItemsState.map((item) => (
                    <div key={item.id} className="flex items-start space-x-4 p-4 border border-gray-200 rounded-lg">
                      <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        {item.customImage ? (
                          <img
                            src={item.customImage}
                            alt={item.product.name}
                            className="w-full h-full object-cover rounded-lg"
                          />
                        ) : (
                          <span className="text-2xl">📦</span>
                        )}
                      </div>

                      <div className="flex-1 min-w-0 flex flex-col gap-4">
                        <div className="min-w-0">
                          <div className="flex items-start justify-between gap-3">
                            <h3 className="text-lg font-medium text-gray-900">{item.product.name}</h3>
                            <p className="text-lg font-bold whitespace-nowrap" style={{ color: '#2e3a47' }}>${item.product.basePrice}</p>
                          </div>
                          <p className="text-sm text-gray-600">
                            Size: {item.size || 'Standard'}, Color: {item.color || 'Default'}
                          </p>
                        </div>

                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => decreaseQuantity(item.id, item.quantity)}
                              className="w-8 h-8 border border-gray-300 rounded flex items-center justify-center hover:bg-gray-50"
                            >
                              -
                            </button>
                            <span className="w-12 text-center font-medium">{item.quantity}</span>
                            <button
                              onClick={() => increaseQuantity(item.id, item.quantity)}
                              className="w-8 h-8 border border-gray-300 rounded flex items-center justify-center hover:bg-gray-50"
                            >
                              +
                            </button>
                          </div>

                          <div className="text-right">
                            <p className="text-lg font-bold text-gray-900">
                              ${(item.product.basePrice * item.quantity).toFixed(2)}
                            </p>
                            <button
                              onClick={() => removeItem(item.id)}
                              className="text-red-500 hover:text-red-700 text-sm flex items-center justify-end space-x-1"
                            >
                              <TrashIcon className="w-4 h-4" />
                              <span>Remove</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div className="bg-gray-50 rounded-lg p-6 sticky top-4">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h2>

                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Subtotal</span>
                      <span className="font-medium">${subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Shipping</span>
                      <span className="font-medium">
                        {isCalculating ? '...' : `$${shipping.toFixed(2)}`}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Tax</span>
                      <span className="font-medium">
                        {isCalculating ? '...' : `$${tax.toFixed(2)}`}
                      </span>
                    </div>
                    {couponApplied && (
                      <div className="flex justify-between text-green-600">
                        <span>Discount</span>
                        <span>-${couponDiscount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="border-t border-gray-200 pt-3">
                      <div className="flex justify-between">
                        <span className="text-lg font-semibold">Total</span>
                        <span className="text-lg font-bold" style={{ color: '#2e3a47' }}>${total.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Coupon Code */}
                  <div className="border-t border-gray-200 pt-4 mb-4">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                      <input
                        type="text"
                        placeholder="Coupon code"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value)}
                        className="w-full flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                        style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                        disabled={couponApplied}
                      />
                      {!couponApplied ? (
                        <button
                          type="button"
                          onClick={handleApplyCoupon}
                          className="w-full sm:w-auto px-4 py-2 text-white rounded-md transition-colors"
                          style={{ backgroundColor: '#2e3a47' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
                        >
                          Apply
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={handleRemoveCoupon}
                          className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    {couponApplied && (
                      <p className="mt-2 text-sm text-green-600">
                        Coupon applied! Discount: ${couponDiscount.toFixed(2)}
                      </p>
                    )}
                  </div>

                  <Link
                    to="/checkout"
                    className="block w-full text-white py-3 px-4 rounded-lg transition-colors font-medium mb-3 text-center"
                    style={{ backgroundColor: '#2e3a47' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
                  >
                    Proceed to Checkout
                  </Link>

                  <Link
                    to="/products"
                    className="block w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-200 transition-colors font-medium text-center"
                  >
                    Continue Shopping
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CartPage; 
